import { useEffect, useState } from "react";
import api, { fmtErr } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { toast } from "sonner";
import { Upload, BookOpen, Tags } from "lucide-react";

const inputCls = "w-24 bg-white border border-neutral-300 rounded-md px-2 py-1.5 text-sm focus:ring-2 focus:ring-neutral-900 outline-none";
const API = process.env.REACT_APP_BACKEND_URL;

const CoverCell = ({ project }) => {
  const [bump, setBump] = useState(0);
  const [failed, setFailed] = useState(false);
  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/projects/${project.id}/cover`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Thumbnail updated for "${project.title}"`);
      setFailed(false);
      setBump((b) => b + 1);
    } catch (err) { toast.error(fmtErr(err)); }
    e.target.value = "";
  };
  return (
    <div className="flex items-center gap-2">
      {failed ? (
        <div className="w-10 h-14 rounded border border-dashed border-neutral-300 flex items-center justify-center"><BookOpen size={13} className="text-neutral-300" /></div>
      ) : (
        <img key={bump} src={`${API}/api/store/cover/${project.id}?v=${bump}`} alt=""
          onError={() => setFailed(true)} className="w-10 h-14 object-cover rounded border border-neutral-200" />
      )}
      <label className="inline-flex items-center gap-1 px-2 py-1.5 border border-neutral-300 rounded-md text-xs font-medium text-neutral-700 hover:bg-neutral-50 cursor-pointer" title="Upload thumbnail">
        <Upload size={11} />
        <input data-testid={`admin-cover-upload-${project.id}`} type="file" accept=".jpg,.jpeg,.png,.webp" className="hidden" onChange={upload} />
      </label>
    </div>
  );
};

const smallInput = "w-20 bg-white border border-neutral-300 rounded-md px-2 py-1 text-xs focus:ring-2 focus:ring-neutral-900 outline-none";

const MarketingPricingEditor = () => {
  const [cat, setCat] = useState([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/marketing/catalog").then((r) => setCat(r.data)); }, []);

  const upd = (pi, key, i, field, v) =>
    setCat((c) => c.map((p, idx) => (idx !== pi ? p : { ...p, [key]: p[key].map((x, j) => (j === i ? { ...x, [field]: v } : x)) })));

  const updShip = (pi, field, v) =>
    setCat((c) => c.map((p, idx) => (idx !== pi ? p : { ...p, shipping: { ...p.shipping, [field]: v } })));

  const save = async () => {
    setBusy(true);
    try {
      const products = cat.map((p) => ({
        ...p,
        sizes: p.sizes.map((s) => ({ ...s, mult: parseFloat(s.mult) })),
        materials: p.materials.map((m) => ({ ...m, mult: parseFloat(m.mult) })),
        tiers: p.tiers.map((t) => ({ ...t, price: parseFloat(t.price) })),
        shipping: { base: parseFloat(p.shipping?.base ?? 0), per_unit: parseFloat(p.shipping?.per_unit ?? 0) },
      }));
      const { data } = await api.put("/admin/marketing/catalog", { products });
      setCat(data);
      toast.success("Marketing pricing updated — live on the marketing page");
    } catch (e) { toast.error(fmtErr(e)); }
    setBusy(false);
  };

  return (
    <div className="bg-white border border-neutral-200 rounded-lg mt-8 p-6" data-testid="marketing-pricing-section">
      <h3 className="font-semibold font-heading flex items-center gap-2"><Tags size={17} /> Marketing pricing</h3>
      <p className="text-sm text-neutral-500 mt-1 mb-5">Base price per quantity tier; size and material multipliers adjust the final total. Changes apply instantly to the public and author marketing pages.</p>
      <div className="grid lg:grid-cols-2 gap-6">
        {cat.map((p, pi) => (
          <div key={p.id} className="border border-neutral-200 rounded-xl p-4" data-testid={`pricing-product-${p.id}`}>
            <p className="font-semibold text-sm font-heading">{p.name}</p>
            <p className="text-xs font-medium text-neutral-500 mt-3 mb-1.5">Quantity tiers ($)</p>
            <div className="flex flex-wrap gap-2">
              {p.tiers.map((t, i) => (
                <label key={t.qty} className="text-xs text-neutral-500 flex items-center gap-1">
                  ×{t.qty}
                  <input data-testid={`mp-price-${p.id}-${t.qty}`} type="number" step="0.01" min="0.01" value={t.price}
                    onChange={(e) => upd(pi, "tiers", i, "price", e.target.value)} className={smallInput} />
                </label>
              ))}
            </div>
            <p className="text-xs font-medium text-neutral-500 mt-3 mb-1.5">Size multipliers</p>
            <div className="flex flex-wrap gap-2">
              {p.sizes.map((s, i) => (
                <label key={s.id} className="text-xs text-neutral-500 flex items-center gap-1">
                  {s.label}
                  <input data-testid={`mp-size-${p.id}-${s.id}`} type="number" step="0.01" min="0.01" value={s.mult}
                    onChange={(e) => upd(pi, "sizes", i, "mult", e.target.value)} className={smallInput} />
                </label>
              ))}
            </div>
            <p className="text-xs font-medium text-neutral-500 mt-3 mb-1.5">Material multipliers</p>
            <div className="flex flex-wrap gap-2">
              {p.materials.map((m, i) => (
                <label key={m.id} className="text-xs text-neutral-500 flex items-center gap-1">
                  {m.label}
                  <input data-testid={`mp-mat-${p.id}-${m.id}`} type="number" step="0.01" min="0.01" value={m.mult}
                    onChange={(e) => upd(pi, "materials", i, "mult", e.target.value)} className={smallInput} />
                </label>
              ))}
            </div>
            <p className="text-xs font-medium text-neutral-500 mt-3 mb-1.5">Shipping</p>
            <div className="flex flex-wrap gap-2">
              <label className="text-xs text-neutral-500 flex items-center gap-1">
                Base $
                <input data-testid={`mp-ship-base-${p.id}`} type="number" step="0.01" min="0" value={p.shipping?.base ?? 0}
                  onChange={(e) => updShip(pi, "base", e.target.value)} className={smallInput} />
              </label>
              <label className="text-xs text-neutral-500 flex items-center gap-1">
                + per extra unit $
                <input data-testid={`mp-ship-unit-${p.id}`} type="number" step="0.01" min="0" value={p.shipping?.per_unit ?? 0}
                  onChange={(e) => updShip(pi, "per_unit", e.target.value)} className={smallInput} />
              </label>
            </div>
          </div>
        ))}
      </div>
      <p className="text-xs text-neutral-400 mt-4">Shipping charged per order: base fee + per-extra-unit fee × (quantity − 1). Shown as a separate line at checkout.</p>
      <button data-testid="marketing-pricing-save" onClick={save} disabled={busy}
        className="mt-5 px-5 py-2 rounded-md text-white bg-neutral-900 hover:bg-neutral-800 text-sm font-medium disabled:opacity-50">
        {busy ? "Saving..." : "Save pricing"}
      </button>
    </div>
  );
};

export default function StoreAdmin() {
  const { user } = useAuth();
  const [projects, setProjects] = useState([]);
  const [orders, setOrders] = useState([]);
  const [mOrders, setMOrders] = useState([]);

  const load = () => {
    api.get("/projects").then((r) => setProjects(r.data.filter((p) => p.status !== "draft")));
    api.get("/admin/store/orders").then((r) => setOrders(r.data));
    api.get("/admin/marketing/orders").then((r) => setMOrders(r.data));
  };
  useEffect(load, []);

  const setMStatus = async (o, status) => {
    try {
      await api.put(`/admin/marketing/orders/${o.id}/status`, { status });
      toast.success(`Order #${o.id.slice(0, 8).toUpperCase()} → ${status.replace(/_/g, " ")} — buyer notified`);
      load();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const setField = (id, k, v) => setProjects((ps) => ps.map((p) => (p.id === id ? { ...p, [k]: v } : p)));

  const save = async (p) => {
    try {
      await api.put(`/admin/projects/${p.id}/store`, {
        store_enabled: !!p.store_enabled,
        store_price: p.store_price ? parseFloat(p.store_price) : null,
        author_copy_price: p.author_copy_price ? parseFloat(p.author_copy_price) : null });
      toast.success(`Store settings saved for "${p.title}"`);
      load();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  return (
    <div data-testid="store-admin-page">
      <div className="mb-8">
        <h1 className="text-3xl font-bold tracking-tight text-neutral-900 font-heading">Bookstore</h1>
        <p className="text-neutral-500 mt-1">Set retail prices for the public store and author-copy prices (taxes & shipping included).</p>
      </div>

      <div className="bg-white border border-neutral-200 rounded-lg overflow-hidden mb-8">
        <div className="overflow-x-auto"><table className="min-w-full divide-y divide-neutral-200 text-sm text-left">
          <thead><tr className="bg-neutral-50">
            {["Thumbnail", "Book", "Author", "In store", "Retail price", "Author copy price", ""].map((h, i) => (
              <th key={i} className="px-6 py-3 font-medium text-neutral-500 uppercase tracking-wider text-xs">{h}</th>))}
          </tr></thead>
          <tbody className="divide-y divide-neutral-100">
            {projects.map((p) => (
              <tr key={p.id} data-testid={`store-row-${p.id}`}>
                <td className="px-6 py-3"><CoverCell project={p} /></td>
                <td className="px-6 py-3 font-medium text-neutral-900">{p.title}</td>
                <td className="px-6 py-3">{p.author_name}</td>
                <td className="px-6 py-3">
                  <input type="checkbox" data-testid={`store-enabled-${p.id}`} checked={!!p.store_enabled}
                    onChange={(e) => setField(p.id, "store_enabled", e.target.checked)} />
                </td>
                <td className="px-6 py-3">
                  <input data-testid={`store-price-${p.id}`} type="number" step="0.01" value={p.store_price ?? ""}
                    onChange={(e) => setField(p.id, "store_price", e.target.value)} className={inputCls} placeholder="19.99" />
                </td>
                <td className="px-6 py-3">
                  <input data-testid={`author-copy-price-${p.id}`} type="number" step="0.01" value={p.author_copy_price ?? ""}
                    onChange={(e) => setField(p.id, "author_copy_price", e.target.value)} className={inputCls} placeholder="8.50" />
                </td>
                <td className="px-6 py-3">
                  <button data-testid={`store-save-${p.id}`} onClick={() => save(p)}
                    className="px-3 py-1.5 rounded-md text-white bg-neutral-900 hover:bg-neutral-800 text-xs font-medium">Save</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table></div>
      </div>

      <div className="bg-white border border-neutral-200 rounded-lg overflow-hidden">
        <h3 className="font-semibold font-heading p-6 pb-2">Orders</h3>
        {orders.length === 0 ? <p className="p-6 pt-2 text-sm text-neutral-400">No orders yet.</p> : (
          <div className="overflow-x-auto"><table className="min-w-full divide-y divide-neutral-200 text-sm text-left">
            <thead><tr className="bg-neutral-50">
              {["Date", "Type", "Book", "Author", "Qty", "Total", "Author earning", "Ship to", "Status"].map((h) => (
                <th key={h} className="px-6 py-3 font-medium text-neutral-500 uppercase tracking-wider text-xs">{h}</th>))}
            </tr></thead>
            <tbody className="divide-y divide-neutral-100">
              {orders.map((o) => (
                <tr key={o.id} data-testid={`order-row-${o.id}`}>
                  <td className="px-6 py-3 whitespace-nowrap">{new Date(o.created_at).toLocaleString()}</td>
                  <td className="px-6 py-3">{o.type === "book_sale" ? "Store sale" : "Author copies"}</td>
                  <td className="px-6 py-3 font-medium">{o.title}</td>
                  <td className="px-6 py-3">{o.author_name}</td>
                  <td className="px-6 py-3">{o.quantity}</td>
                  <td className="px-6 py-3">${o.total.toFixed(2)}</td>
                  <td className="px-6 py-3 text-emerald-700 font-medium">{o.author_earning != null ? `$${o.author_earning.toFixed(2)}` : "—"}</td>
                  <td className="px-6 py-3 text-xs text-neutral-500">
                    {o.shipping ? <>{o.shipping.name}<br />{o.shipping.address1}, {o.shipping.city} {o.shipping.postal_code}</> : "—"}
                  </td>
                  <td className="px-6 py-3">{o.status}</td>
                </tr>
              ))}
            </tbody>
          </table></div>
        )}
      </div>

      <div className="bg-white border border-neutral-200 rounded-lg overflow-hidden mt-8" data-testid="marketing-orders-section">
        <h3 className="font-semibold font-heading p-6 pb-2">Marketing orders</h3>
        {mOrders.length === 0 ? <p className="p-6 pt-2 text-sm text-neutral-400">No marketing orders yet.</p> : (
          <div className="overflow-x-auto"><table className="min-w-full divide-y divide-neutral-200 text-sm text-left">
            <thead><tr className="bg-neutral-50">
              {["Date", "Product", "Options", "Qty", "Total", "Buyer", "Ship to", "Artwork", "Status"].map((h) => (
                <th key={h} className="px-6 py-3 font-medium text-neutral-500 uppercase tracking-wider text-xs">{h}</th>))}
            </tr></thead>
            <tbody className="divide-y divide-neutral-100">
              {mOrders.map((o) => (
                <tr key={o.id} data-testid={`marketing-admin-row-${o.id}`}>
                  <td className="px-6 py-3 whitespace-nowrap">{new Date(o.created_at).toLocaleDateString()}</td>
                  <td className="px-6 py-3 font-medium">{o.product_name}</td>
                  <td className="px-6 py-3 text-neutral-500 text-xs">
                    {o.size_label} · {o.material_label}
                    {o.design_name ? <><br />Design: <b>{o.design_name}</b>{o.book_title ? <> — “{o.book_title}”</> : null}</> : null}
                    {o.notes ? <><br />“{o.notes}”</> : null}
                  </td>
                  <td className="px-6 py-3">{o.quantity}</td>
                  <td className="px-6 py-3 font-semibold">${o.total.toFixed(2)}
                    {o.shipping_cost != null && <div className="text-[10px] font-normal text-neutral-400">incl. ${Number(o.shipping_cost).toFixed(2)} ship</div>}
                  </td>
                  <td className="px-6 py-3">{o.user_email}</td>
                  <td className="px-6 py-3 text-xs text-neutral-500">
                    {o.shipping ? <>{o.shipping.name}<br />{o.shipping.address1}, {o.shipping.city} {o.shipping.postal_code}</> : "—"}
                  </td>
                  <td className="px-6 py-3">
                    {o.artwork_filename ? (
                      <button data-testid={`artwork-download-${o.id}`}
                        onClick={() => window.open(`${API}/api/admin/marketing/orders/${o.id}/artwork`, "_blank")}
                        className="text-xs text-blue-700 hover:underline">{o.artwork_filename}</button>
                    ) : <span className="text-xs text-neutral-300">pending</span>}
                  </td>
                  <td className="px-6 py-3">
                    <select data-testid={`marketing-status-${o.id}`} value={o.status}
                      onChange={(e) => setMStatus(o, e.target.value)}
                      className="border border-neutral-300 rounded-md px-2 py-1 text-xs bg-white">
                      {["paid", "in_production", "shipped", "completed"].map((s) => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table></div>
        )}
      </div>

      {user?.role === "super_admin" && <MarketingPricingEditor />}
    </div>
  );
}
